import apiClient from './apiClient';
import {
  MAX_DATASET_UPLOAD_BYTES,
  formatDatasetUploadMaxSizeLabel,
  validateDatasetUploadFilename,
} from '../constants/datasetUploadLimits';

export const datasetApi = {
  getDatasets: async () => {
    const response = await apiClient.get('/datasets');

    // Slim list shape from GET /datasets (detail via getDatasetMetadata).
    return response.data.map(d => ({
      id: d.id,
      name: d.name,
      type: d.type,
      status: d.status,
      created_at: d.created_at,
      summary: d.summary ?? null,
      stats: d.stats || {},
      column_count: d.column_count ?? 0,
      has_spatial: Boolean(d.has_spatial),
      has_temporal: Boolean(d.has_temporal),
      size: 'N/A',
      date: 'Recent',
    }));
  },

  deleteDataset: async (id) => {
    const response = await apiClient.delete(`/datasets/metadata/${id}`);
    return response.data;
  },

  getUploadUrl: async (filename, contentType) => {
    const suffixError = validateDatasetUploadFilename(filename);
    if (suffixError) {
      throw new Error(suffixError);
    }
    const response = await apiClient.post('/datasets/upload-url', {
      filename,
      content_type: contentType || 'application/octet-stream'
    });
    return response.data;
  },

  inspectUpload: async (storagePath, filename, options = {}) => {
    const { layerName, sourceSrid } = options;
    const response = await apiClient.post(
      '/datasets/upload-inspect',
      {
        storage_path: storagePath,
        filename,
        layer_name: layerName ?? null,
        source_srid: sourceSrid ?? null,
      },
      { validateStatus: (status) => (status >= 200 && status < 300) || status === 422 }
    );
    if (response.status === 422) {
      const err = new Error(response.data?.detail || response.data?.message || 'Upload validation failed');
      err.response = response;
      throw err;
    }
    return response.data.spatial_plan;
  },

  finalizeUpload: async (storagePath, filename, tableId = null, options = {}) => {
    const { layerName, sourceSrid, spatialPlan } = options;
    const response = await apiClient.post(
      '/datasets/upload-finalize',
      {
        storage_path: storagePath,
        filename,
        table_id: tableId,
        layer_name: layerName ?? null,
        source_srid: sourceSrid ?? null,
        spatial_plan: spatialPlan ?? null,
      },
      { validateStatus: (status) => (status >= 200 && status < 300) || status === 422 }
    );
    if (response.status === 422) {
      const err = new Error(response.data?.detail || response.data?.message || 'Upload validation failed');
      err.response = response;
      throw err;
    }
    return response.data;
  },

  uploadToSignedUrl: async (url, file, onProgress, options = {}) => {
    const maxBytes = options.maxUploadBytes ?? MAX_DATASET_UPLOAD_BYTES;
    if (file && typeof file.size === 'number' && file.size > maxBytes) {
      return Promise.reject(
        new Error(`File exceeds maximum size (${formatDatasetUploadMaxSizeLabel(maxBytes)}).`)
      );
    }
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

      xhr.upload.onprogress = (event) => {
        if (!event.lengthComputable || !onProgress) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          onProgress?.(100);
          resolve(true);
          return;
        }
        reject(new Error(`Upload to storage failed: ${xhr.statusText || xhr.status}`));
      };

      xhr.onerror = () => {
        reject(new Error('Upload to storage failed. Please check your connection and try again.'));
      };

      xhr.send(file);
    });
  },

  importBigQueryTables: async (data, idempotencyKey = null, requestOptions = {}) => {
    const response = await apiClient.post('/datasets/bigquery', data, {
      headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {},
      suppressGlobalErrorToast: requestOptions.suppressGlobalErrorToast,
    });
    return response.data;
  },

  getDatasetMetadata: async (id) => {
    const response = await apiClient.get(`/datasets/metadata/${id}`);
    return response.data;
  },

  getDatasetWorkspaces: async (tableId) => {
    const response = await apiClient.get(`/datasets/${encodeURIComponent(tableId)}/workspaces`);
    return response.data;
  },

  updateDatasetMetadata: async (id, data, requestOptions = {}) => {
    const response = await apiClient.patch(`/datasets/metadata/${id}`, data, {
      suppressGlobalErrorToast: requestOptions.suppressGlobalErrorToast,
    });
    return response.data;
  },

  rebuildMetadata: async (table_id) => {
    const response = await apiClient.post(`/datasets/${table_id}/rebuild`);
    return response.data;
  },

  getDistinctValues: async (tableId, column) => {
    const response = await apiClient.get(`/datasets/${tableId}/distinct`, {
      params: { column }
    });
    return response.data.values;
  },

  getColumnRange: async (tableId, column) => {
    const response = await apiClient.get(`/datasets/${tableId}/range`, {
      params: { column }
    });
    return response.data;
  },

  getSamples: async () => {
    const response = await apiClient.get('/datasets/samples');
    return response.data;
  },

  addSample: async (tableId, idempotencyKey = null) => {
    const response = await apiClient.post(
      `/datasets/samples/${tableId}`,
      null,
      {
        headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {},
        suppressGlobalErrorToast: true,
      }
    );
    return response.data;
  },

  downloadParquet: async (tableId, displayName) => {
    const response = await apiClient.get(`/datasets/${tableId}/download`, {
      responseType: 'blob',
      suppressGlobalErrorToast: true,
    });
    const disposition = response.headers['content-disposition'];
    let filename = `${tableId}.parquet`;
    const match = disposition && disposition.match(/filename="([^"]+)"/);
    if (match) {
      filename = match[1];
    } else if (displayName) {
      const base = displayName.replace(/[^\w.-]+/g, '_').replace(/^\.+|\.+$/g, '') || tableId;
      filename = base.endsWith('.parquet') ? base : `${base}.parquet`;
    }
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'application/vnd.apache.parquet' })
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
